import { useState } from 'react';

import { useRedemptionState } from '@/features/code-redemption';
import { Button, NotebookSheet, useToastStore } from '@/shared/ui';

import { triggerDownload } from '../hooks/useDownloadReward';

import styles from './DownloadCard.module.css';

/**
 * 3/3 완료 시 노출되는 보상 다운로드 카드.
 *
 * 서버는 코드 활성화 시점에 `downloadUrl` 을 함께 발급하므로 별도 보상 요청 호출은 없다.
 * 사용자가 [다운로드] 버튼을 누르면 redemption state 의 `downloadUrl` 응답 본문을 fetch 하여
 * 즉시 디스크로 저장(`Blob` + `<a download>`)한다. 새 탭은 열리지 않으며,
 * 네트워크/CORS 실패 시 에러 토스트로 안내한다.
 *
 * URL 이 아직 비어 있는(서버 동기화 전) 짧은 순간에는 안내 토스트만 노출하고,
 * 다운로드 진행 중에는 버튼이 다시 클릭되지 않도록 비활성화한다.
 */
export function DownloadCard(): React.JSX.Element {
  const { state } = useRedemptionState();
  const pushToast = useToastStore((s) => s.push);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async (): Promise<void> => {
    if (state.downloadUrl === null) {
      pushToast({ variant: 'info', message: '다운로드 링크를 준비 중이에요.' });
      return;
    }

    setIsDownloading(true);
    try {
      await triggerDownload(state.downloadUrl);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : '다운로드에 실패했어요.';
      pushToast({ variant: 'error', message });
    } finally {
      setIsDownloading(false);
    }
  };

  const isLinkPending = state.downloadUrl === null;
  const isDisabled = isLinkPending || isDownloading;

  const buttonLabel = ((): string => {
    if (isLinkPending) {
      return '링크 준비 중…';
    }
    if (isDownloading) {
      return '다운로드 중…';
    }
    return '🎨 다운로드';
  })();

  return (
    <NotebookSheet marginColor="none" ruled={false} className={styles.card}>
      <div className={styles.heading}>
        <span className={styles.headingTitle}>다운로드</span>
        <span className={styles.headingStep}>3 / 3</span>
      </div>

      <p className={styles.eyebrow}>완성!</p>
      <h2 className={styles.title}>코드를 모두 모았어요 🎉</h2>
      <p className={styles.body}>
        완성된 다락 유치원 특전 일러스트를 다운로드 받아주세요. <br></br>
        7일간 유효하며 이후로는 다운로드가 불가능합니다.
      </p>

      <div className={styles.actions}>
        <Button
          variant="primary"
          onClick={() => {
            void handleDownload();
          }}
          disabled={isDisabled}
          aria-label="합동 일러스트 다운로드"
          fullWidth
        >
          {buttonLabel}
        </Button>
      </div>
    </NotebookSheet>
  );
}
